import { DESTINATION_GROUP, NAVIGATION_ORIGIN, NAVIGATION_TRIGGER_TYPE } from 'root/lib/SearchlightDispatcher';
import { v4 as uuidv4 } from 'uuid';

import { getMockedUsageData } from 'lib/browserStorage';
import { getTimestamp } from 'root/lib/DatetimeManager';
import { PRODUCT_READING } from 'root/lib/constants';
import { getBenchmarkEOYPath } from 'a/placementTool/redux/selectors';
import { markUsageProgressSeen } from 'a/actions/usageActions';
import { getSelectedProduct } from 'a/selectors/appSessionSelectors';
import { getSelectedProductUsage } from 'a/selectors/usageSelectors';
import { getProduct } from 'a/selectors/languageSelectors';

import createActionObj from '../../actions/createActionObj';
import gameIds from '../data/GameIds';
import {
  saveGameState,
  debouncedSaveGameState,
} from '../../actions/persistStateActions';
import { goToHubScreen } from './metaGameActions';
import { GO_TO_MAP_DELAY, SEASON, GRADES, FORMATTED_GRADES } from '../data/constants';
import {
  reportQuestStart,
  reportQuestComplete,
  reportQuestStepComplete,
  reportQuestStepFailure,
  reportSideQuestEnd,
  reportQuestStepAutoCompleteOn3Failures,
  reportRewardEarned,
} from './metagameSearchlight';
import { dataConverter } from '../../../lib/utilityFunctions';
import STEP_TYPE from '../data/StepTypes';
import saveQuestSession from '../../api/questSession';

export const createMetaGameActionObj = (type, payload) =>
  createActionObj(gameIds.METAGAME, type, payload);

export const goToPractice = ({ trigger = 'not set' }) => (dispatch) => {
  const navigationParams = {
    destinationGroup: DESTINATION_GROUP.PRACTICE,
    origin: NAVIGATION_ORIGIN.MAP,
    trigger,
    triggerType: NAVIGATION_TRIGGER_TYPE.MANUAL,
  };
  dispatch(goToHubScreen({ screenName: 'Curioso', screenParams: { selectedTab: 5 }, navigationParams }));
};

/*
* Function to calculate number of bites required to earn
* 1 augmentation based on Quest index
* Rules:
* Quests 1-5 require 3 bites for 1 augmentation
* Quests 6-11 require 6 bites for 1 augmentation
* Quests 12-17 require 9 bites for 1 augmentation
* Quests 18-23 require 18 bites for 1 augmentation
* Quest 24 requires 24 bites for 1 augmentation
*/
export const calculateRewardCriteria = (questId, season, challengeQuestSteps = []) => {
  let currentQuestId = questId;
  if (currentQuestId === 100 && challengeQuestSteps.length > 0) {
    return challengeQuestSteps.reduce((totalLevels, { levels }) => totalLevels + levels.length, 0);
  }
  if (season === SEASON.GRADE_2_3) {
    currentQuestId -= 200;
  }
  if (currentQuestId >= 1 && currentQuestId < 18) {
    return (parseInt(currentQuestId / 6, 10) + 1) * 3;
  }
  if (currentQuestId >= 18 && currentQuestId < 24) {
    return 18;
  }
  return 24;
};

export const setQuestNotebookData = () => (dispatch, getState) => {
  const { notebookReducer } = getState();
  dispatch(saveGameState(
    gameIds.METAGAME_QUEST_NOTEBOOK,
    dataConverter({
      lastMasteredId: notebookReducer.lastMasteredId,
      quests: notebookReducer.quests,
    }),
  ));
};

export const saveSettingsData = () => (dispatch, getState) => {
  const { questWorkflowReducer } = getState();
  return dispatch(saveGameState(
    gameIds.STUDENT_SETTINGS,
    {
      ...((questWorkflowReducer.gamesData[gameIds.STUDENT_SETTINGS] || {}).GameData || {}),
      language: questWorkflowReducer.language,
      captions: questWorkflowReducer.captions,
    },
  ));
};

export const enablePlacticeModeInTeacherDashboardData = (isPracticeMode = true) => (dispatch, getState) => {
  const { questWorkflowReducer } = getState();
  dispatch(saveGameState(
    gameIds.TEACHER_DASHBOARD_SETTINGS,
    {
      ...((questWorkflowReducer.gamesData[gameIds.TEACHER_DASHBOARD_SETTINGS] || {}).GameData || {}),
      isPracticeMode,
    },
  ));
};

export const saveCuriosoSelectionData = () => (dispatch, getState) => {
  const { curiosoReducer } = getState();
  return dispatch(saveGameState(
    gameIds.METAGAME_CURIOSO_SELECTED,
    dataConverter({
      curioso: curiosoReducer.curioso,
      introComplete: true,
    }),
  ));
};

export const saveCuriosoAugmentationsSelectedData = () => (dispatch, getState) => {
  const { augmentations: curioso_augmentations } = getState()?.curiosoReducer || {};
  const gameId = gameIds.METAGAME_CURIOSO_AUGMENTATIONS_SELECTED;
  const gameData = dataConverter({
    curioso_augmentations,
  });
  dispatch(debouncedSaveGameState({
    gameId,
    gameData,
  }));
};

export const saveCuriosoAugmentationsEarnedData = () => (dispatch, getState) => {
  const { curiosoReducer } = getState();
  dispatch(saveGameState(
    gameIds.METAGAME_CURIOSO_AUGMENTATIONS_EARNED,
    dataConverter({
      curioso_lockedFeatures: curiosoReducer.lockedFeatures,
    }),
  ));
};

export const saveCuriosoItemsData = () => (dispatch, getState) => {
  const { itemsReducer } = getState();
  dispatch(saveGameState(
    gameIds.METAGAME_CURIOSO_ITEMS,
    dataConverter({
      curiosoItems: itemsReducer.curiosoItems,
    }),
  ));
};

export const saveCuriosoPowersData = () => (dispatch, getState) => {
  const { powerReducer } = getState();
  dispatch(saveGameState(
    gameIds.METAGAME_CURIOSO_POWERS,
    dataConverter({
      curiosoPower: powerReducer.curiosoPower,
    }),
  ));
};

export const saveSideQuestData = () => (dispatch, getState) => {
  const { questWorkflowReducer } = getState();
  dispatch(saveGameState(
    gameIds.METAGAME_SIDE_QUEST,
    dataConverter({
      sideQuest: questWorkflowReducer.sideQuest,
    }),
  ));
};

export const saveChallengeQuestData = () => (dispatch, getState) => {
  const state = getState();

  dispatch(
    saveGameState(
      gameIds.METAGAME_CHALLENGE_QUEST,
      dataConverter({
        challengeQuest: state.questWorkflowReducer.quests.quest100,
        areAllQuestsAndChallengesComplete:
          state.questWorkflowReducer.areAllQuestsAndChallengesComplete,
      }),
    ),
  );
  dispatch(saveMetagameQuestData());
};

export const saveMapAreasData = () => (dispatch, getState) => {
  const { questWorkflowReducer } = getState();
  // Debounce gamestate save to batch multiple map updates within 1500ms
  dispatch(debouncedSaveGameState({
    gameId: gameIds.METAGAME_MAP_AREAS,
    gameData: dataConverter({
      mapAreas: questWorkflowReducer.mapAreas,
    }),
  }, { debounceMs: 1500 }));
};

export const saveQuestFailuresData = () => (dispatch, getState) => {
  const { questWorkflowReducer } = getState();
  return dispatch(saveGameState(
    gameIds.METAGAME_QUEST_FAILURES,
    dataConverter({
      stepFailures: questWorkflowReducer.stepFailures,
    }),
  ));
};

export const saveMetagameQuestData = () => (dispatch, getState) => {
  const state = getState();
  const { questWorkflowReducer, curiosoReducer, mapReducer, placementTool } = state;
  const benchmarkEOYPath = getBenchmarkEOYPath(state);

  if (benchmarkEOYPath) {
    return Promise.resolve();
  }

  const promises = [];
  // save quests to server
  promises.push(dispatch(saveGameState(
    gameIds.METAGAME,
    dataConverter({
      currentActiveLocation: questWorkflowReducer.currentActiveLocation,
      currentActiveQuestId: questWorkflowReducer.currentActiveQuestId,
      bitesEarnedInQuest: questWorkflowReducer.bitesEarnedInQuest,
      didEndOfQuestsPlay: questWorkflowReducer.didEndOfQuestsPlay,
      isPostQ1InteractionEnabled: questWorkflowReducer.isPostQ1InteractionEnabled,
      isNotesTabVisible: questWorkflowReducer.isNotesTabVisible,
      unlockedGlyphPowers: questWorkflowReducer.unlockedGlyphPowers,
      lastEarnedAugmentation: curiosoReducer.lastEarnedAugmentation,
      ...(questWorkflowReducer.season && { seasonSaved: questWorkflowReducer.season }),
      lastQuestFinishedBeforeEndOfQuests: questWorkflowReducer.lastQuestFinishedBeforeEndOfQuests,
      lasperHouseLocationIntroPlayed: mapReducer.lasperHouseLocationIntroPlayed,
      placement: questWorkflowReducer.placement,
      localIPTEndReadingTime: placementTool.metrics.localIPTEndReadingTime,
      localIPTEndLecturaTime: placementTool.metrics.localIPTEndLecturaTime,
    }),
  )));
  // test if curioso selection was saved to bucket, override with current reducer data if not
  if (questWorkflowReducer.currentActiveQuestId > 1
    && questWorkflowReducer.introComplete === false
    && (questWorkflowReducer.gamesData[gameIds.METAGAME_CURIOSO_SELECTED] === undefined
      || (((questWorkflowReducer.gamesData[gameIds.METAGAME_CURIOSO_SELECTED] || {}).GameData
        || {}).gameDetails || {}).curioso === undefined)) {
    promises.push(saveCuriosoSelectionData(dispatch, getState));
  }

  return Promise.all(promises);
};

export const saveCurrentQuestData = () => (dispatch, getState) => {
  const {
    quests,
    currentActiveQuestId,
    contentServiceQuestId,
    readingQuestStepsCache,
    lecturaQuestStepsCache,
  } = getState().questWorkflowReducer;
  const currentQuestData = quests[`quest${currentActiveQuestId}`] || {};
  const newQuestSessionId = uuidv4();
  const questSessionId = currentQuestData.questSessionId
    || (currentQuestData.questIntroPlayed ? newQuestSessionId : '');
  // save quests to server
  dispatch(saveGameState(
    gameIds.METAGAME_QUEST_CURRENT,
    dataConverter({
      currentQuest: {
        [`quest${currentActiveQuestId}`]: {
          ...currentQuestData,
          questSessionId,
          updated: getTimestamp(),
        },
      },
      contentServiceQuestId,
      readingQuestStepsCache,
      lecturaQuestStepsCache,
    }),
  ));
  if (currentActiveQuestId && currentQuestData.questIntroPlayed && currentQuestData.questSessionId === '') {
    const contentIds = [];
    let source = 'hard-coded';
    currentQuestData.steps.forEach((game) => {
      contentIds.push(game.skillId);
      if (game.source === 'adaptivity') {
        source = 'adaptivity';
      }
    });
    dispatch(setQuestSessionId(newQuestSessionId));
    saveQuestSession(currentActiveQuestId, newQuestSessionId, `{${contentIds.join()}}`, source);
  }
  // update quest specific data
  dispatch(saveMetagameQuestData());
  // update Map locations state
  dispatch(saveMapAreasData());
};

export const saveQuestDataToHistory = () => (dispatch, getState) => {
  const { quests, currentActiveQuestId, questsOrder, season } = getState().questWorkflowReducer;
  const archivedQuests = {};
  let questPointer = 1;
  if (season === SEASON.GRADE_K_1) {
    if (currentActiveQuestId !== 1 && currentActiveQuestId < 25) {
      questPointer = 1;
    }
  } else if (season === SEASON.GRADE_2_3) {
    if (currentActiveQuestId !== 201 && currentActiveQuestId < 225) {
      questPointer = 201;
    }
  }
  // write data to history for Seasons 1 and 2
  if (season !== SEASON.GRADE_4_5) {
    do {
      archivedQuests[`quest${questPointer}`] = quests[`quest${questPointer}`];
      questPointer = questsOrder[`quest${questPointer}`].nextQuestId;
    } while (parseInt(questPointer, 10) !== parseInt(currentActiveQuestId, 10));
    // save quests to server
    dispatch(saveGameState(
      gameIds.METAGAME_QUEST_HISTORY,
      dataConverter({
        history: {
          ...archivedQuests,
        },
      }),
    ));
  }
};

export const updateVocabStep = () => (dispatch, getState) => {
  const state = getState();
  const { currentActiveQuestId, placement } = state.questWorkflowReducer;
  const { isVocabAppAvailable } = state.workshopReducer;
  const product = getProduct(state);
  const { grade } = state.persistStateReducer;
  const currentQuestSteps = state.questWorkflowReducer.quests[`quest${currentActiveQuestId}`].steps;
  const vocab = currentQuestSteps.find(step => step.type === STEP_TYPE.VOCAB);

  // Vocab can be enabled in Quests for Grade 3 users with Grade 3 placement only
  if (
    grade === GRADES.GRADE_3
    && placement.includes(FORMATTED_GRADES[grade])
    && product === PRODUCT_READING
    && currentActiveQuestId !== 0
  ) {
    // if Vocab App is available, check if we need to add it to Quest
    if (isVocabAppAvailable) {
      // check if vocab is in the current quest, if not put it in
      if (!vocab) {
        // let app update last step of Quest with Vocab
        const setVocabAsGameStep = createMetaGameActionObj('SET_VOCAB_AS_GAME_STEP', {});
        dispatch(setVocabAsGameStep);
      }
    } else if (vocab) {
      // if Vocab is finished - skip this
      if (vocab.levels.length !== vocab.levelsMastered.length) {
        // if Vocab is not finished - replace it with game
        const resetVocabToGameStep = createMetaGameActionObj('RESET_VOCAB_TO_GAME_STEP', {});
        dispatch(resetVocabToGameStep);
      }
    }
  }
};

const hasAugmentationsToUnlock = (lockedFeatures) => {
  const augmentationsLocked = [];
  const types = Object.keys(lockedFeatures);
  types.forEach((elem) => {
    const bodyPart = Object.keys(lockedFeatures[elem]);
    bodyPart.forEach((part) => {
      if (lockedFeatures[elem][part] === true) {
        augmentationsLocked.push({ type: elem, part });
      }
    });
  });
  return augmentationsLocked.length > 0;
};

export const notifyGameLevelFailed = (game, level) => (dispatch, getState) => {
  let bitesEarnedInQuest = parseInt(getState().questWorkflowReducer.bitesEarnedInQuest, 10);
  const { selectedActivity, questWorkflowReducer: { season, quests } } = getState();
  const { questId, isSideQuest, skillId, questLevels, isChallengeQuest } = selectedActivity.params;

  // only award bite for failure in regular quest not challenge quest
  if (!isChallengeQuest) {
    bitesEarnedInQuest += 1;
    // give 1 bite award
    rewardBites(dispatch, 1);
  }
  const bitesNeededForReward = calculateRewardCriteria(
    questId,
    season,
    quests.quest100.steps,
  );
  if (!isSideQuest && hasAugmentationsToUnlock(getState().curiosoReducer.lockedFeatures)
    && bitesEarnedInQuest >= bitesNeededForReward) {
    // pay bites for rewarded augmentation
    payBitesForAugmentation(dispatch, bitesNeededForReward);
    // show reward screen
    showRewardScreen(dispatch);
  }

  // notify user failed level
  const notifyFailure = createMetaGameActionObj('NOTIFY_STEP_FAILURE_QUEST', {
    game,
    level,
    season,
  });
  dispatch(notifyFailure);

  // check if we need to show failures screen
  if (parseInt(getState().questWorkflowReducer.stepFailures[`${game}_${level}`], 10) >= 3) {
    const showFailure = createMetaGameActionObj('SHOW_THREE_FAILURES_SCREEN', {});
    dispatch(showFailure);
    if (!isSideQuest) {
      const completeFailedGame = createMetaGameActionObj('COMPLETE_FAILED_GAME', {
        game,
        questIndex: questId,
        skillId,
        levels: questLevels,
      });
      dispatch(completeFailedGame);
      const questStepFailureReport =
          reportQuestStepFailure({ questId: `${questId}`, gameName: game, levelFailed: level });
      dispatch(questStepFailureReport);
      const questStepAutoCompleteOn3Failures = reportQuestStepAutoCompleteOn3Failures(
        {
          questId: questId.toString(),
          autoCompletedGame: game,
          failedLevel: level.toString(),
          season,
        },
      );
      dispatch(questStepAutoCompleteOn3Failures);
    }
  }
  // update stepFailures
  dispatch(saveQuestFailuresData());
  // update current quest data

  if (isChallengeQuest) {
    dispatch(saveChallengeQuestData());
  } else {
    dispatch(saveCurrentQuestData());
  }
};

export const setQuestSessionId = newQuestSessionId =>
  createMetaGameActionObj('SET_QUEST_SESSION_ID', {
    newQuestSessionId,
  });

export const clearQuestSessionId = () =>
  createMetaGameActionObj('CLEAR_QUEST_SESSION_ID', {});

export const clearQuestStepsFromProductCache = () =>
  createMetaGameActionObj('CLEAR_QUEST_STEPS_FROM_PRODUCT_CACHE', {});

const setLastStepToAnimate = (dispatch, game, location) => {
  const setLastStepToAnimateAction = createMetaGameActionObj('SET_LAST_STEP_TO_ANIMATE', {
    game,
    location,
  });
  dispatch(setLastStepToAnimateAction);
  dispatch(saveMapAreasData());
};

const setLevelMastered = (dispatch, game, questIndex, levelMastered, levels, skillId, product) => {
  const levelMasteredAction = createMetaGameActionObj('SET_QUEST_STEP_LEVEL_MASTERED', {
    game,
    questIndex,
    levelMastered,
    levels,
    skillId,
    product,
  });
  dispatch(levelMasteredAction);
};

const setSideQuestLevelMastered = (dispatch, game, levelMastered, levels, skillId) => {
  const sideQuestStepLevelMasteredAction = createMetaGameActionObj('SET_SIDE_QUEST_STEP_LEVEL_MASTERED', {
    game,
    levelMastered,
    levels,
    skillId,
  });
  dispatch(sideQuestStepLevelMasteredAction);
  dispatch(saveSideQuestData());
};

const rewardBites = (dispatch, bites) => {
  const rewardBitesAction = createMetaGameActionObj('ADD_BITES_EARNED_IN_QUEST', {
    bites,
  });
  dispatch(rewardBitesAction);
};

const setQuestStepComplete = (dispatch, questIndex, game) => {
  const questStepCompleteAction = createMetaGameActionObj('SET_QUEST_STEP_COMPLETE', {
    questIndex,
    game,
  });
  dispatch(questStepCompleteAction);
};

const setAllQuestStepsComplete = (dispatch, questIndex, game) => {
  const questStepsCompleteAction = createMetaGameActionObj('SET_ALL_QUEST_STEPS_COMPLETE', {
    questIndex,
    game,
  });

  dispatch(questStepsCompleteAction);
};

export const hideChallengeQuestPopup = () => (dispatch, getState) => {
  const product = getProduct(getState());
  const hideChallengeQuestPopupAction = createMetaGameActionObj(
    'HIDE_CHALLENGE_QUEST_POPUP',
    { product },
  );
  dispatch(hideChallengeQuestPopupAction);
};

const showRewardScreen = (dispatch) => {
  const showRewardScreenAction = createMetaGameActionObj('SHOW_AUGMENTATION_REWARD_SCREEN', {});
  dispatch(showRewardScreenAction);
};

export const clearQuestFailuresData = () => (dispatch) => {
  const clearQuestFailuresDataAction = createMetaGameActionObj('CLEAR_QUEST_FAILURES_BUCKET', {});
  dispatch(clearQuestFailuresDataAction);
};

const payBitesForAugmentation = (dispatch, bites) => {
  const payBitesForAugmentationEarnedAction = createMetaGameActionObj('REDUCE_BITES_EARNED_IN_QUEST', {
    bites,
  });
  dispatch(payBitesForAugmentationEarnedAction);
};

/**
 * Update the server with user's progress in a quest
 * @param {string} game - the id of the game in which a level of a quest step was completed
 * @param {integer} levelMastered - the index (zero-based) of the level that was mastered
 */
export const notifyGameLevelSuccess = (game, levelMastered) => (dispatch, getState) => {
  const state = getState();
  const { questWorkflowReducer, selectedActivity } = state;
  const product = getProduct(state);
  const { location, questId, isSideQuest, skillId, questLevels } = selectedActivity.params;

  const questSteps = isSideQuest ?
    questWorkflowReducer.sideQuest.games : questWorkflowReducer.quests[`quest${questId}`].steps;
  // find game user just played by game name
  let gameSteps = questSteps.filter((step, index) => {
    if (step.skillId === undefined) {
      return skillId === index;
    }
    return step.skillId === skillId;
  });

  if (isSideQuest) {
    gameSteps = questSteps.filter(step => step.game === game);
  }

  const allLevelsMasteredUnique = new Set([...gameSteps[0].levelsMastered, levelMastered]);
  const allLevelsMastered = [...allLevelsMasteredUnique];
  let bitesEarnedInQuest = parseInt(questWorkflowReducer.bitesEarnedInQuest, 10);
  bitesEarnedInQuest += 1;

  if (isSideQuest) {
    // set step level mastered
    setSideQuestLevelMastered(dispatch, game, levelMastered, questLevels, skillId);
  } else {
    // set which step progress to animate
    setLastStepToAnimate(dispatch, game, location);
    // set step level mastered
    setLevelMastered(dispatch, game, questId, levelMastered, questLevels, skillId, product);
  }

  // save stepsFailures data to store
  dispatch(saveQuestFailuresData());

  // give 1 bite award
  rewardBites(dispatch, 1);

  const bitesNeededForReward = calculateRewardCriteria(
    questId,
    questWorkflowReducer.season,
    questWorkflowReducer.quests.quest100.steps,
  );
  if (!isSideQuest && hasAugmentationsToUnlock(getState().curiosoReducer.lockedFeatures)
    && bitesEarnedInQuest >= bitesNeededForReward) {
    // pay N bites for augmentation earned
    payBitesForAugmentation(dispatch, bitesNeededForReward);
    // show reward screen
    showRewardScreen(dispatch);
  }

  // check if length of mastered levels is bigger then needed and close game
  // this should handle broken accounts if they exist already
  if (allLevelsMastered.length >= getState().selectedActivity.params.questLevels.length) {
    if (!isSideQuest) {
      const allQuestsStepsInLocation = [
        ...getState().questWorkflowReducer.quests[`quest${questId}`].steps,
      ];
      const notMasteredSteps = allQuestsStepsInLocation.filter(step =>
        !(step.levelsMastered.length >= step.levels.length));

      const masteredSteps = allQuestsStepsInLocation.filter(step =>
        (step.levelsMastered.length >= step.levels.length));

      const questStepCompleteReport = reportQuestStepComplete({ location, questId, stepId: masteredSteps.length });
      dispatch(questStepCompleteReport);

      if (notMasteredSteps.length === 0) {
        // complete all quest steps
        setAllQuestStepsComplete(dispatch, questId, game);
      } else {
        // set quest step complete when all step levels are mastered
        setQuestStepComplete(dispatch, questId, game);
      }
    } else {
      // show reward screen if we have what to show
      let delayForClosingSideQuest = 100;
      if (hasAugmentationsToUnlock(getState().curiosoReducer.lockedFeatures)) {
        showRewardScreen(dispatch);
        delayForClosingSideQuest = 4000;
      }
      setTimeout(() => {
        // side quest is finished - disable it
        const reportSideQuestEndAction = reportSideQuestEnd({
          selectedGame: questWorkflowReducer.sideQuest.selectedGame,
          levels: allLevelsMastered,
          isSuccess: true,
          skipped: false,
        });
        dispatch(reportSideQuestEndAction);
        dispatch(disableSideQuest());
        dispatch(saveMetagameQuestData());
        dispatch(saveSideQuestData());
        // writing and reading data at the same time may result in dirty data
        // that's why we has this delay here
        setTimeout(() => {
          const navigationParams = {
            destinationGroup: DESTINATION_GROUP.HUB,
            origin: NAVIGATION_ORIGIN.SIDE_QUEST_POPUP,
            trigger: 'sideQuestFinishedEvent',
            triggerType: NAVIGATION_TRIGGER_TYPE.AUTOMATIC,
          };
          dispatch(goToHubScreen({ screenName: 'Map', navigationParams }));
        }, GO_TO_MAP_DELAY);
      }, delayForClosingSideQuest);
    }
  }
  if (!isSideQuest) {
    if (selectedActivity.params.isChallengeQuest) {
      dispatch(saveChallengeQuestData());
    } else {
      dispatch(saveCurrentQuestData());
    }
  }
};

export const notifyBookSuccess = (gameId) => (dispatch, getState) => {
  const state = getState();
  const { selectedActivity: { params: { questId, skillId, isChallengeQuest } } } = state;

  dispatch(createMetaGameActionObj('COMPLETE_EREADER_STEP', {
    gameId, skillId, questIndex: questId,
  }));

  if (isChallengeQuest) {
    dispatch(saveChallengeQuestData());
  } else {
    dispatch(saveCurrentQuestData());
  }
};

export const setQuestStepIntroCompleted = (questIndex, index) => (dispatch, getState) => {
  const { questWorkflowReducer } = getState();
  const { quests: { [`quest${questIndex}`]: { questIntroPlayed } } } = questWorkflowReducer;

  const introCompleteAction = createMetaGameActionObj('SET_QUEST_STEP_INTRO_COMPLETE', {
    questIndex,
    index,
  });
  dispatch(introCompleteAction);

  if (index === 1 && !questIntroPlayed) {
    const { questsOrder } = questWorkflowReducer;
    const questLocation = questsOrder[`quest${questIndex}`].location;
    // searchlight reporting for starting quest
    const questStartedAction = reportQuestStart({
      location: questLocation,
      questId: questIndex,
    });
    dispatch(questStartedAction);
  }

  dispatch(saveCurrentQuestData());
};

export const setEndOfQuestsSeen = () => (dispatch) => {
  const endOfQuestsSeenAction = createMetaGameActionObj('SET_END_OF_QUEST_SEEN', {});
  dispatch(endOfQuestsSeenAction);

  dispatch(saveMetagameQuestData());
};

export const disablePostQ1Interaction = () => (dispatch) => {
  const disableCustomInteraction = createMetaGameActionObj('SET_HIDE_CUSTOM_INTERACTION', {});
  dispatch(disableCustomInteraction);

  dispatch(saveMetagameQuestData());
};

export const completeS2Q1 = () => (dispatch) => {
  const enableCustomInteraction = createMetaGameActionObj('SET_SHOW_CUSTOM_INTERACTION', {});
  const showNotesTabAction = createMetaGameActionObj('SET_NOTES_TAB_VISIBILITY', {});

  dispatch(enableCustomInteraction);
  dispatch(showNotesTabAction);
  dispatch(saveMetagameQuestData());
};

export const setNextQuestLocationActive = currentQuestId => (dispatch, getState) => {
  const state = getState();
  const { questWorkflowReducer } = state;
  const product = getProduct(state);
  const { questsOrder } = questWorkflowReducer;
  // find next quest to play
  const { nextQuestId, location } = questsOrder[`quest${currentQuestId}`];
  let nextLocation = '';

  if (nextQuestId) {
    nextLocation = questsOrder[`quest${nextQuestId}`].location;
  }
  dispatch(clearQuestSessionId());
  dispatch(clearQuestStepsFromProductCache());
  dispatch(clearQuestFailuresData());
  dispatch(saveQuestFailuresData());
  const locationActiveAction = createMetaGameActionObj('SET_QUEST_LOCATION_ACTIVE', {
    location: nextLocation,
    questId: nextQuestId,
    product,
  });
  dispatch(locationActiveAction);
  dispatch(showSideQuestAvailabilityPopup());
  // update current quest with new data
  dispatch(saveCurrentQuestData());
  // move completed quest to history data
  dispatch(saveQuestDataToHistory());
  // searchlight reporting for quest completion
  const questCompleteReport = reportQuestComplete({ location, questId: currentQuestId });
  dispatch(questCompleteReport);
};

export const setQuestLocationOpened = currentQuestId => (dispatch, getState) => {
  const { questsOrder } = getState().questWorkflowReducer;
  // find current quest location
  const currentLocation = questsOrder[`quest${currentQuestId}`].location;
  // find next quest to play
  const { nextQuestId } = questsOrder[`quest${currentQuestId}`];
  let nextLocation = '';
  if (nextQuestId) {
    // find location of the next quest
    nextLocation = questsOrder[`quest${nextQuestId}`].location;
  }
  const locationOpenedAction = createMetaGameActionObj('SET_QUEST_LOCATION_OPENED', {
    location: nextLocation,
    previousLocation: currentLocation,
  });
  dispatch(locationOpenedAction);
  // no need to save data as this action always goes before setNextQuestLocationActive which saves data
};

export const showSingleStepMasteredScreen = attributes =>
  createMetaGameActionObj('SHOW_SINGLE_STEP_MASTERED_SCREEN', attributes);

export const hideSingleStepMasteredScreen = attributes =>
  createMetaGameActionObj('HIDE_SINGLE_STEP_MASTERED_SCREEN', attributes);

export const hideStepMasteredScreen = () => (dispatch) => {
  dispatch(hideSingleStepMasteredScreen());
  dispatch(hideAllStepsMasteredScreen());
};

export const showAllStepsMasteredScreen = attributes =>
  createMetaGameActionObj('SHOW_ALL_STEPS_MASTERED_SCREEN', attributes);

export const hideAllStepsMasteredScreen = attributes =>
  createMetaGameActionObj('HIDE_ALL_STEPS_MASTERED_SCREEN', attributes);

export const showThreeFailuresScreen = attributes =>
  createMetaGameActionObj('SHOW_THREE_FAILURES_SCREEN', attributes);

// when stuck game is auto-completed we should reset stuckness counter
export const clearLevelStuckness = (game, levels) => (dispatch) => {
  const clearLevelStucknessAction = createMetaGameActionObj('CLEAR_LEVEL_STUCKNESS', {
    game,
    levels,
  });
  dispatch(clearLevelStucknessAction);
};

export const hideThreeFailuresScreen = (game, levels) => (dispatch) => {
  dispatch(clearLevelStuckness(game, levels));
  return Promise.all([
    dispatch(saveQuestFailuresData()),
    dispatch(saveMetagameQuestData()),
  ]).then(() => dispatch(createMetaGameActionObj('HIDE_THREE_FAILURES_SCREEN')));
};

export const preventPlayingFailureVOAgain = attributes =>
  createMetaGameActionObj('PREVENT_FAILURE_VO_PLAYING', attributes);

export const addBitesEarnedWhenLevelIsMasteredInQuest = bites =>
  createMetaGameActionObj('ADD_BITES_EARNED_IN_QUEST', {
    bites,
  });

export const setNotebookProgress = attributes => (dispatch) => {
  const setNotebookProgressAction = createMetaGameActionObj('SET_NOTEBOOK_PROGRESS', attributes);
  dispatch(setNotebookProgressAction);
  dispatch(setQuestNotebookData());
};

export const showAugmentationRewardScreen = attributes =>
  createMetaGameActionObj('SHOW_AUGMENTATION_REWARD_SCREEN', attributes);

export const hideAugmentationRewardScreen = attributes =>
  createMetaGameActionObj('HIDE_AUGMENTATION_REWARD_SCREEN', attributes);

export const reduceBitesEarnedInQuest = bites =>
  createMetaGameActionObj('REDUCE_BITES_EARNED_IN_QUEST', {
    bites,
  });

export const clearLastStepToAnimate = location => (dispatch) => {
  const clearLastStepToAnimateAction = createMetaGameActionObj('CLEAR_LAST_STEP_TO_ANIMATE', {
    location,
  });
  dispatch(clearLastStepToAnimateAction);
  dispatch(saveMapAreasData());
};

export const setLocationProgress = (location, progress) => (dispatch) => {
  const setLocationProgressAction = createMetaGameActionObj('SET_LOCATION_PROGRESS', {
    location,
    progress,
  });
  dispatch(setLocationProgressAction);
  dispatch(saveMapAreasData());
};

export const enableCuriosoPower = power => (dispatch) => {
  const enableCuriosoPowerAction = createMetaGameActionObj('ENABLE_POWER', {
    power,
  });
  dispatch(enableCuriosoPowerAction);
  dispatch(saveCuriosoPowersData());
};

export const addCuriosoItem = item => (dispatch) => {
  const addCuriosoItemAction = createMetaGameActionObj('ADD_CURIOSO_ITEM', {
    item,
  });
  dispatch(addCuriosoItemAction);
  dispatch(saveCuriosoItemsData());
};

export const unlockGlyphPower = item => (dispatch) => {
  dispatch(createMetaGameActionObj(
    'UNLOCK_GLYPH_POWER',
    {
      item,
    },
  ));
};

export const hideSideQuestGamesPopup = () => (dispatch) => {
  const hideSideQuestGamesPopupAction = createMetaGameActionObj('HIDE_SIDE_QUEST_GAME_POPUP', {});
  dispatch(hideSideQuestGamesPopupAction);
  dispatch(saveSideQuestData());
};

export const showSideQuestGamesPopup = () => (dispatch) => {
  const showSideQuestGamesPopupAction = createMetaGameActionObj('SHOW_SIDE_QUEST_GAME_POPUP', {});
  dispatch(showSideQuestGamesPopupAction);
  dispatch(saveSideQuestData());
};

export const showSideQuestAvailabilityPopup = () => (dispatch) => {
  const showSideQuestAvailabilityPopupAction = createMetaGameActionObj('SHOW_SIDE_QUEST_AVAILABILITY_POPUP', {});
  dispatch(showSideQuestAvailabilityPopupAction);
  dispatch(saveSideQuestData());
};

export const hideSideQuestAvailabilityPopup = () => (dispatch) => {
  const hideSideQuestAvailabilityPopupAction = createMetaGameActionObj('HIDE_SIDE_QUEST_AVAILABILITY_POPUP', {});
  dispatch(hideSideQuestAvailabilityPopupAction);
  dispatch(saveSideQuestData());
};

export const disableSideQuest = () => (dispatch, getState) => {
  const disableSideQuestAction = createMetaGameActionObj('DISABLE_SIDE_QUEST', {});
  dispatch(disableSideQuestAction);
  const { questWorkflowReducer } = getState();
  const { quests, currentActiveQuestId } = questWorkflowReducer;
  if (currentActiveQuestId !== 0) {
    const { questIntroPlayed } = quests[`quest${currentActiveQuestId}`];
    if (!questIntroPlayed) {
      dispatch(clearQuestFailuresData());
      dispatch(saveQuestFailuresData());
    }
  }
  dispatch(saveSideQuestData());
};

export const selectSideQuestGame = gameId => (dispatch) => {
  const selectSideQuestGameAction = createMetaGameActionObj('SET_SIDE_QUEST_GAME', {
    gameId,
  });
  dispatch(selectSideQuestGameAction);
  dispatch(saveSideQuestData());
};

export const changeLanguage = language => (dispatch) => {
  const changeLanguageAction = createMetaGameActionObj('CHANGE_LANGUAGE', {
    language,
  });
  dispatch(changeLanguageAction);
  return dispatch(saveSettingsData());
};

export const updateCaptionsAvailability = (areCaptionsBlocked) => (dispatch) =>
  dispatch(createMetaGameActionObj('UPDATE_CAPTIONS_AVAILABILITY', areCaptionsBlocked));

export const enablePracticeLocally = checked =>
  createMetaGameActionObj('ENABLE_PRACTICE_LOCALLY', checked);

export const notifyUserHadAllQuestsCompletePopupShown = () => (dispatch, getState) => {
  const product = getProduct(getState());
  dispatch(
    createMetaGameActionObj('NOTIFY_USER_HAS_QUESTS_COMPLETE_POPUP_SHOWN', { product }),
  );
};

export const updateChallengeQuestData = games => (dispatch) => {
  const updateChallengeQuestDataAction = createMetaGameActionObj('UPDATE_CHALLENGE_QUEST', {
    games,
  });
  dispatch(updateChallengeQuestDataAction);
  return dispatch(saveChallengeQuestData());
};

export const setLastQuestPlayedBeforeEndOfQuests = () =>
  createMetaGameActionObj('SET_LAST_QUEST_PLAYED_BEFORE_END_OF_QUESTS');

export const lockPracticeAndUnlockNewQuest = () => (dispatch) => {
  const unlockNewQuestAction = createMetaGameActionObj('UNLOCK_NEW_QUEST_AVAILABLE', {
  });
  dispatch(unlockNewQuestAction);
  dispatch(enablePlacticeModeInTeacherDashboardData(false));
  dispatch(saveMapAreasData());
  dispatch(saveMetagameQuestData());
};

export const hideNewQuestAvailablePopup = () =>
  createMetaGameActionObj('HIDE_NEW_QUEST_AVAILABLE_POPUP', {});

export const hideRePlacementPopup = () => (dispatch) => {
  const hideRePlacementPopupAction = createMetaGameActionObj('HIDE_RE_PLACEMENT_POPUP', {});
  dispatch(hideRePlacementPopupAction);
  dispatch(saveMetagameQuestData());
};

export const updateEreaderStep = ({ bookId, data }) => createMetaGameActionObj('UPDATE_EREADER_STEP', { bookId, data });

export const setInitialQuestStateForLectura = () => createMetaGameActionObj('SET_INITIAL_QUEST_STATE_FOR_LECTURA');

export const updateQuestStepsFromProductCache = (questStepsCache, product) => (dispatch, getState) => {
  const { questWorkflowReducer } = getState();
  const currentQuest = `quest${questWorkflowReducer.currentActiveQuestId}`;
  const oldSteps = [...questWorkflowReducer.quests[currentQuest].steps];
  // load in all mastered steps first
  const newQuestSteps = oldSteps.filter(step => step.levelsMastered.length >= step.levels.length);
  // get steps of same product from cache
  const stepsToChooseFrom = questStepsCache.filter(
    step => !newQuestSteps.map(placedStep => placedStep.skillId).includes(step.skillId) && step.product === product,
  );
  // split them into in progress, adaptivity and hardcoded
  const inProgressAdaptivitySteps = [];
  const inProgressHardCodedSteps = [];
  const notStartedStepsFromAdaptivity = [];
  const notStartedStepsFromHardCoded = [];
  stepsToChooseFrom.forEach(
    (step) => {
      if (step.levelsMastered.length > 0) {
        if (step.source === 'adaptivity') {
          inProgressAdaptivitySteps.push(step);
        } else {
          inProgressHardCodedSteps.push(step);
        }
      } else if (step.source === 'adaptivity') {
        notStartedStepsFromAdaptivity.push(step);
      } else {
        notStartedStepsFromHardCoded.push(step);
      }
    },
  );
  // if there are enough, fill in the rest and stop. else move on to adaptivity games, then hardcoded last
  [
    ...inProgressAdaptivitySteps,
    ...notStartedStepsFromAdaptivity,
    ...inProgressHardCodedSteps,
    ...notStartedStepsFromHardCoded,
  ].forEach((step) => {
    if (newQuestSteps.length < oldSteps.length) {
      newQuestSteps.push(step);
    }
  });
  const updateQuestStepsFromProductCacheAction = createMetaGameActionObj(
    'UPDATE_QUEST_STEPS_FROM_PRODUCT_CACHE', { newQuestSteps },
  );
  dispatch(updateQuestStepsFromProductCacheAction);
};

export const setCachedProductQuestSteps = (cachedProductQuestSteps, product) => (dispatch) => {
  const setCachedProductQuestStepsAction = createMetaGameActionObj(
    'SET_CACHED_PRODUCT_QUEST_STEPS', { cachedProductQuestSteps, product },
  );
  dispatch(setCachedProductQuestStepsAction);
  dispatch(saveCurrentQuestData());
};

export const setLasperHouseIntroPlayed = attributes => (dispatch) => {
  const lasperIntroAction = createMetaGameActionObj('LASPER_HOUSE_INTRO_PLAYED', attributes);
  dispatch(lasperIntroAction);
  dispatch(saveMetagameQuestData());
};

export const unlockCuriosoAugmentationsList = attributes => (dispatch) => {
  const unlockCuriosoAugmentationAction = createMetaGameActionObj('UNLOCK_CURIOSO_AUGMENTATIONS_LIST', attributes);
  dispatch(unlockCuriosoAugmentationAction);
  /*
    report about earning augmentation
  */
  const unlockAugmentationReport =
    reportRewardEarned({
      rewardType: 'augmentation',
      rewardCount: attributes.list.length,
      rewardInfo: attributes.list[0] ? {
        name: attributes.list[0].part.replace('Locked', ''),
        section: attributes.list[0].type,
      } : {},
    });
  dispatch(unlockAugmentationReport);
  dispatch(saveMetagameQuestData());
  dispatch(saveCuriosoAugmentationsEarnedData());
};

export const setCuriosoAttributes = attributes => (dispatch) => {
  const setCuriosoAttributeAction = createMetaGameActionObj('SET_CURIOSO_ATTRIBUTE', attributes);
  dispatch(setCuriosoAttributeAction);
  dispatch(saveCuriosoSelectionData());
};

const persistUsageData = () => (dispatch, getState) => {
  const state = getState();
  const selectedProduct = getSelectedProduct(state);
  const { lastShownUsageTime, weekKey } = getSelectedProductUsage(state);

  const { questWorkflowReducer } = state;
  const details = ((questWorkflowReducer.gamesData?.[gameIds.METAGAME_USAGE] || {}).GameData || {}).gameDetails || {};

  const newState = {
    ...details,
    weeklyUsage: {
      ...(details.weeklyUsage || {}),
      [selectedProduct]: {
        [weekKey]: {
          lastShownUsageTime,
        },
      },
    },
  };
  dispatch(saveGameState(gameIds.METAGAME_USAGE, dataConverter(newState)));
};

/**
 * Updates Redux state with the latest usage progress and persists it to the gamestate API.
 * Persistence is skipped when mock usage data is active to avoid polluting saved state.
 * @param {object} payload
 * @param {number} payload.lastShownUsageTime - Usage time (in minutes) last shown to the student
 */
export const saveUsageData = (payload) => (dispatch) => {
  const mockData = getMockedUsageData();
  dispatch(markUsageProgressSeen(payload));

  if (!mockData) {
    dispatch(persistUsageData());
  }
};
